import { useCopilotAction } from '@copilotkit/react-core'

import { DataDownloadCard } from '@factorialco/f0-react'
import type { DataDownloadDataset } from '@factorialco/f0-react'

/**
 * Display download buttons for query results. The agent sends the raw
 * dataset (columns + rows) plus an optional markdown preview, and the card
 * generates Excel/CSV files client-side.
 */
export const useDataDownloadAction = () => {
  useCopilotAction({
    name: 'downloadData',
    description:
      'Display download buttons for query results. Sends the raw dataset to the frontend for client-side Excel and CSV generation, optionally with a markdown preview table.',
    parameters: [
      {
        name: 'title',
        type: 'string',
        description:
          "Optional display title for the data card and canvas header, in the user's language. E.g. 'List of employees', 'Monthly sales'. Falls back to filename if not provided.",
        required: false,
      },
      {
        name: 'markdown',
        type: 'string',
        description:
          'Optional markdown content to display above the download buttons (e.g. a table preview, description, or summary)',
        required: false,
      },
      {
        name: 'filename',
        type: 'string',
        description:
          "Descriptive filename (without extension) for the downloaded files, in the user's language. E.g. 'ventas_por_mes', 'team_salaries'.",
        required: false,
      },
      {
        name: 'dataset',
        type: 'object',
        description:
          'Raw dataset for client-side file generation. Contains column headers and row data.',
        required: true,
        attributes: [
          {
            name: 'columns',
            type: 'string[]',
            description: 'Column headers in display order',
            required: true,
          },
          {
            name: 'rows',
            type: 'object[]',
            description: 'Array of row objects keyed by column name',
            required: true,
          },
          {
            name: 'totalCount',
            type: 'number',
            description: 'Total number of rows returned by the query (before truncation)',
            required: false,
          },
          {
            name: 'previewCount',
            type: 'number',
            description: 'Number of rows shown in the markdown preview table',
            required: false,
          },
          {
            name: 'columnLabels',
            type: 'object',
            description:
              "Map of raw column names to human-readable labels in the user's language. Used for Excel/CSV headers.",
            required: false,
          },
        ],
      },
    ],
    available: 'frontend',
    render: ({ args: { title, markdown, filename, dataset } }) => {
      // `dataset` arrives typed as `object` (schema `type: 'object'`); narrow to
      // the component contract at the agent → component trust boundary.
      // eslint-disable-next-line no-type-assertion/no-type-assertion -- agent → component trust boundary; schema-validated upstream
      const resolvedDataset = (dataset ?? { columns: [], rows: [] }) as DataDownloadDataset
      // Guard while streaming — wait for columns to arrive.
      if (!resolvedDataset.columns?.length) return <></>
      return (
        <DataDownloadCard
          title={title}
          dataset={resolvedDataset}
          filename={filename}
          markdown={markdown}
        />
      )
    },
  })
}
